// @givmo/employer-module — the company gift-card notices: which signed notice each send path shows,
// and each one's compiled-in, hash-guarded floor (GC-FU12b). ★ COUNSEL-PINNED LAW ★
//
// A company sending a gift card is shown the notice signed for ITS payment (Jonathan Lesh, President of
// Givmo Charitable Fund, 2026-11-02; the legal system of record is givmo-files):
//   - the COMPANY WALLET → Notice A, `GIFT_CARD_PURCHASE_WALLET` v1
//     (`legal/disclosures/gift-card-company-notices-2026-11.md`, `cd8cda28…`): the wallet's value is
//     already an irrevocable contribution, so a send is no new one — no new receipt, no new deduction,
//     and no fees;
//   - a COMPANY CARD → Notice C, the `GIFT_CARD_PURCHASE` company-payer variant (`applies_to_state`
//     `COMPANY`, resolved with `?payer=company`; `legal/disclosures/gift-card-company-card-notice-2026-11.md`,
//     `5497ee51…`): the company's payment is its own charitable contribution (D-GC-15).
// Each shows on the single and batch send confirms and at the setup of an occasion program funded the
// same way. NEVER the individual purchaser's `GIFT_CARD_PURCHASE` v2 (`48b5b4a2…`) on either path: its
// items 1 and 5 are written for a person's own deduction. Notice B was retired before seeding.
//
// SERVER-DRIVEN, WITH A FLOOR. Both rows are LIVE (backend #4073), so the registry's answer is the
// normal render. A failed resolve — any error, the 404 or 400 of a backend before #4073, an empty body —
// renders the notice's compiled-in floor: the signed bytes verbatim, one item per line, whose sha256
// equals the record's `content_hash` (`gift-disclaimer.test.ts` recomputes it, so any reword reddens
// it). A 200 for another variant (a backend that ignores `payer` answers v2) or for another surface
// (v2 shares Notice A's `NOT_APPLICABLE` variant name, so the variant alone cannot tell them apart)
// counts as failed and renders the floor too, so a dialog never shows a body it did not ask for, and
// never shows none.
// Copy the bytes from the record — never reword them here.
//
// Each rendered notice carries a test-facing marker of which notice it is (`noticeMarker`), which the
// e2e specs read to tell this bundle from the one before it (GC-FU12c).

import { givmoClient } from '@givmo/api-client';
import {
  createDisclosureCache,
  floorDisclosure,
  resolveDisclosure,
  type DisclosureFloor,
  type DisclosurePayer,
  type ResolvedDisclosure,
} from '@givmo/disclosure';
import type { Funding } from './gift-cards-decode';

/** One signed company notice: where the registry keys it, and its floor. */
export interface CompanyGiftCardNotice {
  /** The registry surface the notice is stored under. */
  readonly surface: 'GIFT_CARD_PURCHASE_WALLET' | 'GIFT_CARD_PURCHASE';
  /** Who pays, on a payer-keyed resolve (`company` for Notice C); `null` for none. */
  readonly payer: DisclosurePayer | null;
  /** The registry variant (`applies_to_state`) the notice is stored as: the only one it accepts. */
  readonly state: 'NOT_APPLICABLE' | 'COMPANY';
  /** The signed text, one item per line, and its pinned `content_hash`. */
  readonly floor: DisclosureFloor;
}

/** Notice A — the company-wallet send (`GIFT_CARD_PURCHASE_WALLET` v1), six items, signed 2026-11-02. */
export const COMPANY_WALLET_NOTICE: CompanyGiftCardNotice = {
  surface: 'GIFT_CARD_PURCHASE_WALLET',
  payer: null,
  state: 'NOT_APPLICABLE',
  floor: {
    lines: [
      "Gift cards sent from your company's Givmo wallet are funded with value that is already an irrevocable charitable contribution to Givmo Charitable Fund (a 501(c)(3) donor-advised-fund sponsor, EIN 99-2418877). Sending one is not a new charitable contribution: no new tax receipt is issued, and there is no new charitable deduction.",
      "A charity receives funds only after a gift card's recipient redeems it and recommends a grant. The gift's value can be used only to recommend grants to charities — it is not redeemable for cash or for any merchant's goods or services. If it's never redeemed, no charity receives funds; after about three years the Fund may apply unredeemed gifts to its grantmaking, while always honoring presented gifts.",
      "Timing depends on redemption (there's no deadline). Once redeemed and a grant is recommended, the Fund's normal disbursement schedule applies.",
      "Sending from the wallet has no fees: the wallet is debited exactly each gift card's face amount. No fee is ever deducted from a gift card's face amount.",
      "Sending is final — a gift card can't be cancelled or refunded once it's sent, and its value never returns to the wallet, even if it's never redeemed.",
      'Charity selections are recommendations to Givmo Charitable Fund, which retains exclusive legal control over all grants.',
    ],
    contentHash: 'cd8cda2876d5115a6ea7ee418f28baed38f8a7beb5b9d83fd2150be4214b2f76',
  },
};

/**
 * Notice C — the company-card send (the `GIFT_CARD_PURCHASE` company-payer variant v1), seven items,
 * signed 2026-11-02.
 */
export const COMPANY_CARD_NOTICE: CompanyGiftCardNotice = {
  surface: 'GIFT_CARD_PURCHASE',
  payer: 'company',
  state: 'COMPANY',
  floor: {
    lines: [
      "When your company pays for a gift card with a company card, the payment is an irrevocable donation to Givmo Charitable Fund (a 501(c)(3) donor-advised-fund sponsor, EIN 99-2418877) — not to the recipient's eventual charity.",
      "A charity receives funds only after a gift card's recipient redeems it and recommends a grant. The gift's value can be used only to recommend grants to charities — it is not redeemable for cash or for any merchant's goods or services. If it's never redeemed, no charity receives funds; after about three years the Fund may apply unredeemed gifts to its grantmaking, while always honoring presented gifts.",
      "Timing depends on redemption (there's no deadline). Once redeemed and a grant is recommended, the Fund's normal disbursement schedule applies.",
      "No fee is ever deducted from a gift card's face amount. Card processing is added on top of each gift card's face amount and charged to the company card, as part of the donation; the exact amount charged appears on the receipt.",
      "Each card payment is a charitable contribution by your company, tax-deductible to the extent allowed by law. Whether your business deducts it directly or it passes through to the owners depends on your company's tax structure; please consult your tax advisor.",
      "Each card payment is final — it can't be cancelled or refunded, and nothing is returned to your company, even if the gift card is never redeemed.",
      'Charity selections are recommendations to Givmo Charitable Fund, which retains exclusive legal control over all grants.',
    ],
    contentHash: '5497ee515f15ef4ce0a574069b1be7028ac86536baa48c7345ccf853e3c6662b',
  },
};

/** The notice a send shows for its funding source: Notice A on the wallet, Notice C on a card. */
export function companyGiftCardNotice(funding: Pick<Funding, 'kind'>): CompanyGiftCardNotice {
  return funding.kind === 'wallet' ? COMPANY_WALLET_NOTICE : COMPANY_CARD_NOTICE;
}

// One ETag cache for the session: a reopen revalidates with a conditional GET (304 = headers only).
const cache = createDisclosureCache();

/**
 * Resolve a notice: its LIVE registry row when the answer is that notice's own row — its surface AND
 * its variant — else its floor. Never throws and never `null` — the floor is the signed text, so a
 * notice always renders.
 */
export async function resolveCompanyGiftCardNotice(
  notice: CompanyGiftCardNotice,
): Promise<ResolvedDisclosure> {
  const resolved = await resolveDisclosure({
    client: givmoClient,
    surface: notice.surface,
    ...(notice.payer !== null ? { payer: notice.payer } : {}),
    floor: notice.floor,
    cache,
  });
  if (
    resolved !== null &&
    (resolved.isFloor ||
      (resolved.surface === notice.surface && resolved.appliesToState === notice.state))
  ) {
    return resolved;
  }
  return floorDisclosure(notice.floor);
}

/** The notice as it renders before its resolve answers: the floor, never an empty panel. */
export function companyGiftCardNoticeFloor(notice: CompanyGiftCardNotice): ResolvedDisclosure {
  return floorDisclosure(notice.floor);
}

/** The data attributes a rendered notice carries, naming which notice and which bytes it is. */
export type NoticeMarker = Readonly<Record<`data-${string}`, string>>;

/**
 * The test-facing marker of a rendered notice (GC-FU12b): the registry surface and variant it is, the
 * `content_hash` of the bytes on screen, and whether they came from the registry or the floor. The
 * bundle before this one carries no marker at all.
 */
export function noticeMarker(
  notice: CompanyGiftCardNotice,
  shown: ResolvedDisclosure,
): NoticeMarker {
  return {
    'data-disclosure-surface': notice.surface,
    'data-disclosure-state': notice.state,
    'data-disclosure-hash': shown.contentHash,
    'data-disclosure-source': shown.isFloor ? 'floor' : 'registry',
  };
}
